import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientSectorAPI, leadAPI, settingsAPI, userAPI, noteAPI, followUpAPI, b2bAPI } from '../api';
import { toast } from 'sonner';
import { ClickToCallButton } from '../components/ClickToCallButton';
import { useAuth } from '../context/AuthContext';
import { leadNo, prioBadge as canonicalPrioBadge } from '../lib/leadDisplay';
import { LocationFilter, emptyLocation } from '../components/LocationPicker';
import {
  Plus, Search, MessageSquare, Users, Flame, Clock, AlertCircle, CheckCircle2, XCircle,
  PhoneOff, LayoutGrid, Table2, Calendar, StickyNote, MoreVertical, ChevronLeft, ChevronRight, X, Droplets
, UserX } from 'lucide-react';

const STATUS_OPTS = [['new_lead', 'Active'], ['contacted', 'Contacted'], ['call_back', 'Call Back'],
  ['call_not_connected', 'Call Not Connected'], ['busy', 'Busy'], ['switch_off', 'Switch Off'],
  ['hot_lead', 'Hot Lead'], ['interested', 'Interested'], ['follow_up', 'Follow Up'],
  ['meeting_scheduled', 'Meeting Scheduled'], ['site_visit_scheduled', 'Site Visit Scheduled'],
  ['quotation_sent', 'Quotation Sent'], ['negotiation', 'Negotiation'], ['waiting_approval', 'Client Approval'],
  ['future_prospect', 'Future Prospect'], ['won', 'Won'], ['converted', 'Converted'],
  ['not_interested', 'Not Interested'], ['lost', 'Lost'], ['duplicate', 'Duplicate']];
// Canonical display only: DB keeps its legacy values (urgent/high/medium/low) untouched.
// PRIO + prioBadge now live in ../lib/leadDisplay so the Sales dashboard renders identically.
const LEAK_REASONS = ['Missed Callback', 'Missed Follow-up', 'Missed Meeting', 'Missed Site Visit',
  'Post-Quotation Inactive', 'Negotiation Stale', 'Client Approval Stale', 'No Activity'];
const SALES_STAGES = ['New', 'Contacted', 'Interested', 'Meeting / Site Visit', 'Quotation', 'Negotiation', 'Client Approval', 'Won'];
const KPIS = [['total', 'Total Leads', Users, 'text-slate-700 dark:text-slate-200'], ['new', 'New', Plus, 'text-blue-600'],
  ['hot', 'Hot (Priority)', Flame, 'text-red-600'], ['follow_up_due', 'Follow-up Due', Clock, 'text-amber-600'],
  ['overdue', 'Overdue', AlertCircle, 'text-red-600'], ['leak', 'Leak Leads', Droplets, 'text-rose-600'],
  ['converted', 'Converted', CheckCircle2, 'text-green-600'], ['lost', 'Lost', XCircle, 'text-slate-500'],
  ['unattended', 'Unattended', PhoneOff, 'text-orange-600'],
  ['unassigned', 'Unassigned', UserX, 'text-rose-600']];
const KPI_FILTER = { total: {}, new: { status: 'new_lead' }, hot: { priority: 'hot' }, follow_up_due: { follow_up: 'due_today' },
  overdue: { follow_up: 'overdue' }, leak: { leak: 'true' }, converted: { status: 'converted_group' },
  lost: { status: 'lost_group' }, unattended: { follow_up: 'unattended' },
  unassigned: { assigned: 'unassigned' } };
// Dashboard drill-through: a card's query maps onto the SAME filter state the KPI chips use, so the
// opened list always matches the number that was clicked.
const DRILL_NAMED = { leakage: 'leak', not_contacted: 'unattended', overdue: 'overdue', due_today: 'follow_up_due' };
const DRILL_PASSTHROUGH = ['status', 'assigned', 'lead_source', 'assigned_to', 'date_from', 'date_to'];
const drillFilters = (sp) => {
  const out = {};
  const named = DRILL_NAMED[sp.get('filter')];
  if (named) Object.assign(out, KPI_FILTER[named]);
  DRILL_PASSTHROUGH.forEach((k) => { const v = sp.get(k); if (v) out[k] = v; });
  return out;
};
const EMPTY = { search: '', status: '', stage: '', sales_stage: '', priority: '', assigned_to: '', lead_source: '',
  service: '', client_type: '', client_sector: '', partner_id: '', follow_up: '', leak: '', leak_reason: '', leak_age_min: '',
  date_from: '', date_to: '', created_by: '', assigned: '', ...emptyLocation };
// Location filter keys that exist only to render the selection — the API takes country/state/city.
const LOC_LOCAL_ONLY = ['country', 'state_code', 'district', 'district_code', 'locality'];
const fld = 'w-full px-2.5 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-xs';

const age = (d) => {
  if (!d) return '—';
  const h = (Date.now() - new Date(d).getTime()) / 36e5;
  return h < 24 ? `${Math.max(1, Math.round(h))}h` : `${Math.round(h / 24)}d`;
};
const fuState = (iso) => {
  if (!iso) return ['No Follow-up', 'text-slate-400'];
  const d = new Date(iso); const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const t2 = new Date(today.getTime() + 864e5);
  if (d < today) return [`Overdue · ${d.toLocaleDateString('en-IN')}`, 'text-red-600'];
  if (d < t2) return [`Today ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`, 'text-amber-600'];
  return [`${d.toLocaleDateString('en-IN')} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`, 'text-emerald-600'];
};
const prioBadge = canonicalPrioBadge;
const lastAct = (iso, type) => (iso ? `${(type || 'activity').replace(/_/g, ' ')} · ${age(iso)} ago` : 'No activity');

const LeakChip = ({ lead }) => (!lead.is_leak ? null : (
  <div data-testid={`leak-chip-${lead.id}`} title={`Leak since ${new Date(lead.leak_since).toLocaleString('en-IN')}`}
    className="mt-0.5 inline-block rounded bg-rose-50 px-1.5 py-0.5 text-rose-700 dark:bg-rose-900/30">
    <span className="text-[10px] font-semibold">LEAK · {lead.leak_age_days ?? 0}d</span>
    <span className="block text-[10px] leading-tight">{lead.leak_reason}</span>
  </div>
));

export const LeadsPage = () => {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const [view, setView] = useState(() => localStorage.getItem('b5d_view') || 'table');
  const [isDesktop, setIsDesktop] = useState(() => (typeof window === 'undefined' ? true : window.innerWidth >= 1024));
  // Seed straight from the URL so the FIRST request is already filtered (no unfiltered flash/race).
  const [filters, setFilters] = useState(() => ({ ...EMPTY, ...drillFilters(new URLSearchParams(window.location.search)) }));
  const [sort, setSort] = useState(() => (new URLSearchParams(window.location.search).get('sort') === 'ageing' ? 'oldest' : 'newest'));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [kpis, setKpis] = useState({});
  const [loading, setLoading] = useState(true);
  const [opts, setOpts] = useState({ sources: [], services: [], users: [], partners: [], stages: [] });
  const [selected, setSelected] = useState([]);
  const [bulk, setBulk] = useState({ action: '', value: '', reason: '' });
  const [modal, setModal] = useState(null); // {type:'note'|'followup'|'reassign'|'status', lead}
  const [menu, setMenu] = useState(null);

  const activeFilters = useMemo(() => Object.entries(filters).filter(([, v]) => v), [filters]);
  // chips show human location names, not the ISO/GeoNames codes behind them
  const chipFilters = useMemo(() => activeFilters.filter(([k]) => !['country_code', 'state_code', 'district_code'].includes(k)), [activeFilters]);
  const clearFilter = (k) => {
    if (k === 'country') setFilters((f) => ({ ...f, ...emptyLocation }));
    else if (k === 'state') setFilters((f) => ({ ...f, state_code: '', state: '', district_code: '', district: '', city: '' }));
    else set(k, '');
  };
  const params = useMemo(() => {
    const p = { page, page_size: pageSize, sort };
    activeFilters.forEach(([k, v]) => {
      if (LOC_LOCAL_ONLY.includes(k)) return;
      if (k === 'country_code') { p.country = v; return; }
      p[k] = v;
    });
    return p;
  }, [page, pageSize, sort, activeFilters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, k] = await Promise.all([leadAPI.getPaginated(params), leadAPI.kpis()]);
      setData(list.data);
      setKpis(k.data);
    } catch (e) { toast.error('Failed to load leads'); }
    finally { setLoading(false); }
  }, [params]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { localStorage.setItem('b5d_view', view); }, [view]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { setModal(null); setMenu(null); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  useEffect(() => {
    Promise.all([
      settingsAPI.getLeadSources().catch(() => ({ data: [] })),
      clientSectorAPI.list().catch(() => ({ data: [] })),
      settingsAPI.getServices().catch(() => ({ data: [] })),
      userAPI.getAll().catch(() => ({ data: [] })),
      b2bAPI.list().catch(() => ({ data: { partners: [] } })),
      leadAPI.workflowStages ? leadAPI.workflowStages().catch(() => ({ data: [] })) : Promise.resolve({ data: [] })
    ]).then(([s, sec, sv, u, p, st]) => setOpts({
      sources: s.data || [], sectors: sec.data || [], services: sv.data || [], users: u.data || [],
      partners: p.data?.partners || [], stages: (st.data || []).map((x) => x.name || x)
    }));
  }, []);

  const set = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(1); setSelected([]); };
  const applyKpi = (key) => { setFilters({ ...EMPTY, ...KPI_FILTER[key] }); setPage(1); setSelected([]); };
  const clearAll = () => { setFilters(EMPTY); setPage(1); setSelected([]); };

  const toggleSel = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSel = data.items.length > 0 && selected.length === data.items.length;
  const showTable = view === 'table' && isDesktop;

  const runBulk = async () => {
    if (!bulk.action || selected.length === 0) return;
    const payload = { lead_ids: selected, action: bulk.action };
    if (bulk.action === 'delete') {
      if (!window.confirm(`Move ${selected.length} selected lead(s) to Recycle Bin? Super Admin can restore within 30 days.`)) return;
      try {
        const r = await leadAPI.bulkAction(payload);
        toast.success(`${r.data.updated} lead(s) moved to Recycle Bin`);
        setBulk({ action: '', value: '', reason: '' }); setSelected([]); load();
      } catch (e) { toast.error(e.response?.data?.detail || 'Bulk delete failed'); }
      return;
    }
    if (bulk.action === 'assign') payload.assigned_to = bulk.value;
    if (bulk.action === 'status') { payload.status = bulk.value; payload.status_reason = bulk.reason; }
    if (bulk.action === 'priority') payload.priority = bulk.value;
    if (bulk.action === 'tags') payload.tags = bulk.value.split(',').map((t) => t.trim()).filter(Boolean);
    if (!payload.assigned_to && !payload.status && !payload.priority && !payload.tags?.length) { toast.error('Pick a value'); return; }
    try {
      const r = await leadAPI.bulkAction(payload);
      toast.success(`${r.data.updated} lead(s) updated`);
      setBulk({ action: '', value: '', reason: '' }); setSelected([]); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk action failed'); }
  };

  const quickUpdate = async (lead, patch) => {
    try { await leadAPI.update(lead.id, patch); toast.success('Lead updated'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  // Same canonical permission the backend enforces (leads.delete, or the per-user control).
  const canDeleteLeads = hasPermission('leads.delete') || user?.lead_controls?.can_delete === true;

  const softDelete = async (lead) => {
    if (!window.confirm(`Move ${lead.lead_no || 'this lead'} to Recycle Bin? Super Admin can restore it within 30 days.`)) return;
    try { await leadAPI.delete(lead.id); toast.success('Moved to Recycle Bin'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const Badge = ({ text, cls }) => <span className={`inline-block rounded px-1.5 py-0.5 text-[11px] font-medium ${cls}`}>{text}</span>;

  const Actions = ({ lead, compact }) => (
    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <ClickToCallButton lead={lead} stopPropagation dataTestid={`lead-call-${lead.id}`}
        className="rounded-lg bg-green-50 p-1.5 text-green-700 hover:bg-green-100 disabled:opacity-60 dark:bg-green-900/30" />
      <button data-testid={`lead-whatsapp-${lead.id}`} title="WhatsApp"
        onClick={() => window.open(`https://wa.me/${(lead.whatsapp_number || lead.primary_mobile || '').replace(/\D/g, '')}`)}
        className="rounded-lg bg-emerald-50 p-1.5 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/30"><MessageSquare className="h-3.5 w-3.5" /></button>
      <button data-testid={`lead-followup-${lead.id}`} title="Schedule Follow-up" onClick={() => setModal({ type: 'followup', lead })}
        className="rounded-lg bg-blue-50 p-1.5 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30"><Calendar className="h-3.5 w-3.5" /></button>
      <div className="relative">
        <button data-testid={`lead-menu-${lead.id}`} title="More actions" onClick={() => setMenu(menu === lead.id ? null : lead.id)}
          className="rounded-lg p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800"><MoreVertical className="h-3.5 w-3.5" /></button>
        {menu === lead.id && (
          <div className={`absolute right-0 z-40 mt-1 w-44 overflow-hidden rounded-lg border border-slate-200 bg-white text-left text-xs shadow-lg dark:border-slate-700 dark:bg-slate-900 ${compact ? 'bottom-full mb-1' : ''}`}>
            <button data-testid={`lead-open-${lead.id}`} onClick={() => navigate(`/leads/${lead.id}`)} className="block w-full px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-800">Open Lead 360</button>
            <button data-testid={`lead-note-${lead.id}`} onClick={() => { setMenu(null); setModal({ type: 'note', lead }); }} className="block w-full px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-800">Add Note</button>
            {hasPermission('leads.assign') && <button data-testid={`lead-reassign-${lead.id}`} onClick={() => { setMenu(null); setModal({ type: 'reassign', lead }); }} className="block w-full px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-800">Reassign</button>}
            {(hasPermission('leads.change_status') || hasPermission('leads.edit')) && <button data-testid={`lead-status-${lead.id}`} onClick={() => { setMenu(null); setModal({ type: 'status', lead }); }} className="block w-full px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-800">Change Status</button>}
            {hasPermission('leads.edit') && ['hot', 'warm', 'cold'].map((p) => (
              <button key={p} data-testid={`lead-prio-${p}-${lead.id}`} onClick={() => { setMenu(null); quickUpdate(lead, { priority: p }); }}
                className="block w-full px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-800">Set {prioBadge(p)[0]}</button>
            ))}
            {['won', 'converted'].includes(lead.status) && hasPermission('projects.create') && (
              <button data-testid={`lead-create-project-${lead.id}`} onClick={() => navigate(`/projects/new?lead=${lead.id}`)}
                className="block w-full px-3 py-2 text-left text-primary hover:bg-slate-50 dark:hover:bg-slate-800">
                {(lead.converted_project_ids || []).length ? 'Create Another Project' : 'Create Project'}
              </button>
            )}
            {canDeleteLeads && (
              <button data-testid={`lead-delete-${lead.id}`} onClick={() => { setMenu(null); softDelete(lead); }}
                className="block w-full border-t border-slate-100 px-3 py-2 text-left text-red-600 hover:bg-red-50 dark:border-slate-800 dark:hover:bg-red-900/20">
                Delete Lead
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="leads-container">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-outfit font-semibold lg:text-3xl">All Leads</h1>
          <p className="text-xs text-muted-foreground">Manage, track and convert your leads in one place</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
            <button data-testid="view-table" onClick={() => setView('table')} className={`flex items-center gap-1 px-3 py-2 text-xs ${view === 'table' ? 'bg-primary text-white' : ''}`}><Table2 className="h-3.5 w-3.5" />Table</button>
            <button data-testid="view-card" onClick={() => setView('card')} className={`flex items-center gap-1 px-3 py-2 text-xs ${view === 'card' ? 'bg-primary text-white' : ''}`}><LayoutGrid className="h-3.5 w-3.5" />Cards</button>
          </div>
          {hasPermission('leads.create') && <button data-testid="leads-create-button" onClick={() => navigate('/leads/new')}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm text-white"><Plus className="h-4 w-4" />Add Lead</button>}
        </div>
      </div>

      {/* Management-only Leak breakdown. Same predicate and same scope as the Leak KPI — monitoring only. */}
      {(kpis.leak_by_executive || []).length > 0 && (
        <div data-testid="leak-exec-strip"
          className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-rose-100 bg-rose-50/60 px-3 py-2 text-xs dark:border-rose-900/40 dark:bg-rose-950/20">
          <span className="font-semibold text-rose-700 dark:text-rose-300">Leak Leads: {kpis.leak ?? 0}</span>
          <span className="text-muted-foreground">by executive</span>
          {kpis.leak_by_executive.map((e) => (
            <button key={e.user_id || 'unassigned'} data-testid={`leak-exec-${e.user_id || 'unassigned'}`}
              onClick={() => { setPage(1); setFilters({ ...EMPTY, leak: 'true', assigned_to: e.user_id || '' }); }}
              className="rounded-full border border-rose-200 bg-white px-2 py-0.5 transition-colors hover:bg-rose-100 dark:border-rose-900/40 dark:bg-slate-900">
              {e.name} <span className="font-semibold">{e.count}</span>
            </button>
          ))}
          <span className="text-muted-foreground">· workload, lead sources and unassigned inflow affect these numbers</span>
        </div>
      )}

      {/* KPI strip — every card filters the list to the same records it counted */}
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-5 lg:grid-cols-10" data-testid="kpi-strip">
        {KPIS.filter(([key]) => key !== 'unassigned' || hasPermission('leads.assign')).map(([key, label, Icon, color]) => (
          <button key={key} data-testid={`kpi-${key}`} onClick={() => applyKpi(key)}
            className="rounded-xl border border-slate-200 bg-white p-2.5 text-left transition hover:border-primary dark:border-slate-700 dark:bg-slate-900">
            <Icon className={`h-4 w-4 ${color}`} />
            <div className={`mt-1 text-lg font-semibold ${color}`} data-testid={`kpi-value-${key}`}>{kpis[key] ?? '—'}</div>
            <div className="text-[10px] leading-tight text-muted-foreground">{label}</div>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="space-y-2 rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900" data-testid="filter-bar">
        <div className="grid grid-cols-1 gap-2 md:grid-cols-3 lg:grid-cols-6">
          <div className="relative lg:col-span-2">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <input data-testid="leads-search-input" value={filters.search} onChange={(e) => set('search', e.target.value)}
              placeholder="Search name, mobile, Lead ID or company" className={`${fld} pl-8`} />
          </div>
          <select data-testid="leads-filter-status" value={filters.status} onChange={(e) => set('status', e.target.value)} className={fld}>
            <option value="">All Statuses</option>{STATUS_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select data-testid="leads-filter-sales-stage" value={filters.sales_stage} onChange={(e) => set('sales_stage', e.target.value)} className={fld}>
            <option value="">All Sales Stages</option>{SALES_STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select data-testid="leads-filter-priority" value={filters.priority} onChange={(e) => set('priority', e.target.value)} className={fld}>
            <option value="">All Priorities</option><option value="hot">Hot</option><option value="warm">Warm</option><option value="cold">Cold</option>
          </select>
          <select data-testid="leads-filter-assigned"
            value={filters.assigned === 'unassigned' ? 'unassigned' : filters.assigned_to}
            onChange={(e) => { const v = e.target.value; setPage(1);
              setFilters((f) => ({ ...f, assigned: v === 'unassigned' ? 'unassigned' : '', assigned_to: v === 'unassigned' ? '' : v })); }}
            className={fld}>
            <option value="">All Executives</option>
            <option value="unassigned">Unassigned</option>
            {opts.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <select data-testid="leads-filter-source" value={filters.lead_source} onChange={(e) => set('lead_source', e.target.value)} className={fld}>
            <option value="">All Sources</option>{opts.sources.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
          </select>
          <select data-testid="leads-filter-service" value={filters.service} onChange={(e) => set('service', e.target.value)} className={fld}>
            <option value="">All Services</option>{opts.services.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
          </select>
          <select data-testid="leads-filter-sector" value={filters.client_sector} onChange={(e) => set('client_sector', e.target.value)} className={fld}>
            <option value="">All Sectors</option>
            {(opts.sectors || []).map((x) => <option key={x.sector_id} value={x.sector_id}>{x.name}</option>)}
          </select>
          <select data-testid="leads-filter-type" value={filters.client_type} onChange={(e) => set('client_type', e.target.value)} className={fld}>
            <option value="">B2C + B2B</option><option value="b2c">B2C</option><option value="b2b">B2B</option>
          </select>
          {filters.client_type === 'b2b' && (
            <select data-testid="leads-filter-partner" value={filters.partner_id} onChange={(e) => set('partner_id', e.target.value)} className={fld}>
              <option value="">All Partners</option>{opts.partners.map((p) => <option key={p.id} value={p.id}>{p.company_name || p.contact_person}</option>)}
            </select>
          )}
          <LocationFilter idPrefix="leads-filter-loc" value={filters}
            onChange={(patch) => setFilters((f) => ({ ...f, ...patch }))} />
          <select data-testid="leads-filter-followup" value={filters.follow_up} onChange={(e) => set('follow_up', e.target.value)} className={fld}>
            <option value="">Any Follow-up</option><option value="due_today">Due Today</option><option value="overdue">Overdue</option><option value="unattended">Unattended</option>
            <option value="upcoming">Upcoming</option><option value="none">No Follow-up</option>
          </select>
          <select data-testid="leads-filter-leak" value={filters.leak} onChange={(e) => set('leak', e.target.value)} className={fld}>
            <option value="">Leak: any</option><option value="true">Leak Leads only</option>
          </select>
          {filters.leak === 'true' && <>
            <select data-testid="leads-filter-leak-reason" value={filters.leak_reason} onChange={(e) => set('leak_reason', e.target.value)} className={fld}>
              <option value="">All Leak Reasons</option>{LEAK_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <select data-testid="leads-filter-leak-age" value={filters.leak_age_min} onChange={(e) => set('leak_age_min', e.target.value)} className={fld}>
              <option value="">Any Leak Age</option>{[3, 7, 14, 30].map((n) => <option key={n} value={n}>{`Leak Age ≥ ${n}d`}</option>)}
            </select>
          </>}
          <select data-testid="leads-filter-created-by" value={filters.created_by} onChange={(e) => set('created_by', e.target.value)} className={fld}>
            <option value="">Created by anyone</option>{opts.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <input type="date" data-testid="leads-filter-date-from" value={filters.date_from} onChange={(e) => set('date_from', e.target.value)} className={fld} />
          <input type="date" data-testid="leads-filter-date-to" value={filters.date_to} onChange={(e) => set('date_to', e.target.value)} className={fld} />
          <select data-testid="leads-sort" value={sort} onChange={(e) => { setSort(e.target.value); setPage(1); }} className={fld}>
            <option value="newest">Newest First</option><option value="oldest">Oldest First</option>
            <option value="follow_up">Follow-up Date</option><option value="priority">Priority</option>
            <option value="leak_age">Leak Age (oldest first)</option>
          </select>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {chipFilters.length > 0 && <>
            <span className="text-[11px] text-muted-foreground">Active:</span>
            {chipFilters.map(([k, v]) => (
              <span key={k} data-testid={`active-filter-${k}`} className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] text-amber-700 dark:bg-amber-900/30">
                {k === 'assigned' ? 'assignment' : k.replace(/_/g, ' ')}: {((k === 'assigned_to' || k === 'created_by')
                  && (opts.users.find((u) => u.id === v)?.name)) || String(v).slice(0, 18)}
                <button onClick={() => clearFilter(k)}><X className="h-3 w-3" /></button>
              </span>
            ))}
            <button data-testid="leads-filter-reset" onClick={clearAll} className="rounded-lg border border-slate-200 px-2.5 py-1 text-[11px] dark:border-slate-700">Clear Filters</button>
          </>}
          <span className="ml-auto text-[11px] text-muted-foreground" data-testid="result-count">
            {loading ? 'Loading…' : `Showing ${data.items.length} of ${data.total} leads`}
          </span>
        </div>
      </div>

      {/* Bulk bar */}
      {selected.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-primary/40 bg-primary/5 p-3" data-testid="bulk-bar">
          <span className="text-xs font-medium" data-testid="bulk-count">{selected.length} selected</span>
          <select data-testid="bulk-action" value={bulk.action} onChange={(e) => setBulk({ action: e.target.value, value: '', reason: '' })} className={`${fld} w-40`}>
            <option value="">Bulk action…</option>
            {hasPermission('leads.assign') && <option value="assign">Assign Executive</option>}
            {(hasPermission('leads.change_status') || hasPermission('leads.edit')) && <option value="status">Change Status</option>}
            {hasPermission('leads.edit') && <option value="priority">Change Priority</option>}
            {hasPermission('leads.edit') && <option value="tags">Add Tags</option>}
            {canDeleteLeads && <option value="delete">Delete Selected</option>}
          </select>
          {bulk.action === 'assign' && (
            <select data-testid="bulk-value" value={bulk.value} onChange={(e) => setBulk({ ...bulk, value: e.target.value })} className={`${fld} w-48`}>
              <option value="">Select executive</option>
              {opts.users.filter((u) => u.can_own_leads).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          )}
          {bulk.action === 'status' && <>
            <select data-testid="bulk-value" value={bulk.value} onChange={(e) => setBulk({ ...bulk, value: e.target.value })} className={`${fld} w-44`}>
              <option value="">Select status</option>{STATUS_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            {['lost', 'not_interested'].includes(bulk.value) && (
              <input data-testid="bulk-reason" value={bulk.reason} onChange={(e) => setBulk({ ...bulk, reason: e.target.value })}
                placeholder="Reason (mandatory)" className={`${fld} w-56`} />
            )}
          </>}
          {bulk.action === 'priority' && (
            <select data-testid="bulk-value" value={bulk.value} onChange={(e) => setBulk({ ...bulk, value: e.target.value })} className={`${fld} w-40`}>
              <option value="">Select priority</option><option value="hot">Hot</option><option value="warm">Warm</option><option value="cold">Cold</option>
            </select>
          )}
          {bulk.action === 'tags' && <input data-testid="bulk-value" value={bulk.value} onChange={(e) => setBulk({ ...bulk, value: e.target.value })} placeholder="tag1, tag2" className={`${fld} w-56`} />}
          <button data-testid="bulk-apply" onClick={runBulk} className="rounded-lg bg-primary px-3 py-1.5 text-xs text-white">Apply</button>
          <button data-testid="bulk-clear" onClick={() => setSelected([])} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs dark:border-slate-700">Clear selection</button>
        </div>
      )}

      {/* TABLE VIEW — desktop only; below lg the workspace falls back to cards (never a squeezed table) */}
      {showTable && (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900" data-testid="leads-table">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-left dark:bg-slate-800/50">
              <tr>
                <th className="px-2 py-2"><input type="checkbox" data-testid="select-all" checked={allSel}
                  onChange={() => setSelected(allSel ? [] : data.items.map((l) => l.id))} /></th>
                {['Lead ID', 'Client', 'Contact', 'Requirement', 'Priority', 'Sales Stage', 'Status', 'Assigned', 'Next Follow-up', 'Last Activity', 'Age', ''].map((h) => (
                  <th key={h} className="whitespace-nowrap px-2 py-2 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.items.length === 0 && !loading && <tr><td colSpan={13} className="px-3 py-10 text-center text-muted-foreground">No leads match these filters.</td></tr>}
              {data.items.map((l) => {
                const [fu, fuCls] = fuState(l.next_follow_up || l.follow_up_date);
                const [pl, pc] = prioBadge(l.priority);
                return (
                  <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-t border-slate-100 hover:bg-slate-50/60 dark:border-slate-800 dark:hover:bg-slate-800/40">
                    <td className="px-2 py-2"><input type="checkbox" data-testid={`select-${l.id}`} checked={selected.includes(l.id)} onChange={() => toggleSel(l.id)} /></td>
                    <td className="whitespace-nowrap px-2 py-2">
                      <button data-testid={`lead-id-${l.id}`} onClick={() => navigate(`/leads/${l.id}`)} className="font-mono text-primary hover:underline">{leadNo(l)}</button>
                    </td>
                    <td className="px-2 py-2">
                      <button onClick={() => navigate(`/leads/${l.id}`)} className="font-medium hover:underline">{l.customer_name}</button>
                      {l.client_type === 'b2b' && <div className="text-[10px] text-purple-600" data-testid={`b2b-info-${l.id}`}>B2B · {l.partner_name || 'Partner'} → {l.client_name || '—'}</div>}
                      {l.company_name && l.client_type !== 'b2b' && <div className="text-[10px] text-muted-foreground">{l.company_name}</div>}
                    </td>
                    <td className="whitespace-nowrap px-2 py-2">{l.primary_mobile}<div className="text-[10px] text-muted-foreground">{l.city || '—'}{l.country_code && l.country_code !== 'IN' ? ` · ${l.country_code}` : ''}</div></td>
                    <td className="px-2 py-2">{(l.services_required || []).join(', ') || '—'}</td>
                    <td className="px-2 py-2"><Badge text={pl} cls={pc} /></td>
                    <td className="px-2 py-2" data-testid={`sales-stage-${l.id}`}>{l.sales_stage || '—'}</td>
                    <td className="px-2 py-2">{(STATUS_OPTS.find(([v]) => v === l.status) || [null, l.status])[1]}
                      {l.status_reason && <div className="text-[10px] text-muted-foreground">{l.status_reason}</div>}
                      <LeakChip lead={l} /></td>
                    <td className="px-2 py-2">{l.assigned_to_name || 'Unassigned'}</td>
                    <td className={`whitespace-nowrap px-2 py-2 ${fuCls}`} data-testid={`followup-${l.id}`}>{fu}</td>
                    <td className="whitespace-nowrap px-2 py-2 text-muted-foreground" data-testid={`last-activity-${l.id}`}
                      title={l.last_activity ? new Date(l.last_activity).toLocaleString('en-IN') : 'No activity recorded'}>{lastAct(l.last_activity, l.last_activity_type)}</td>
                    <td className="px-2 py-2" data-testid={`age-${l.id}`}>{age(l.created_at)}</td>
                    <td className="px-2 py-2"><Actions lead={l} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* CARD VIEW (and the responsive fallback for the table on tablet/mobile) */}
      {!showTable && (
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4" data-testid="leads-cards">
        {data.items.map((l) => {
          const [fu, fuCls] = fuState(l.next_follow_up || l.follow_up_date);
          const [pl, pc] = prioBadge(l.priority);
          return (
            <div key={l.id} data-testid="lead-card" onClick={() => navigate(`/leads/${l.id}`)}
              className="cursor-pointer rounded-xl border border-slate-200 bg-white p-3 transition hover:border-primary dark:border-slate-700 dark:bg-slate-900">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate font-medium" data-testid="lead-name">{l.customer_name}</p>
                  <p className="font-mono text-[11px] text-primary">{leadNo(l)}</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Badge text={pl} cls={pc} />
                  <Badge text={(STATUS_OPTS.find(([v]) => v === l.status) || [null, l.status])[1]} cls="bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300" />
                  <LeakChip lead={l} />
                </div>
              </div>
              <div className="mt-2 space-y-1 text-[11px] text-muted-foreground" onClick={(e) => e.stopPropagation()}>
                <p>{l.primary_mobile}{l.city ? ` · ${l.city}` : ''}{l.country_code && l.country_code !== 'IN' ? ` (${l.country_code})` : ''}</p>
                <p>{(l.services_required || []).join(', ') || 'No service selected'}</p>
                {l.client_type === 'b2b' && <p className="text-purple-600" data-testid={`b2b-info-${l.id}`}>B2B · {l.partner_name || 'Partner'} → {l.client_name || '—'}</p>}
                <p>{l.sales_stage ? `${l.sales_stage} · ` : ''}{l.assigned_to_name || 'Unassigned'}</p>
                <p className={fuCls} data-testid={`followup-${l.id}`}>{fu}</p>
                <p data-testid={`last-activity-${l.id}`}>{lastAct(l.last_activity, l.last_activity_type)}</p>
              </div>
              <div className="mt-2 flex items-center justify-between border-t border-slate-100 pt-2 dark:border-slate-800">
                <span className="text-[11px] text-muted-foreground" data-testid={`age-${l.id}`}>Age {age(l.created_at)}</span>
                <div className="flex items-center gap-1">
                  <input type="checkbox" data-testid={`select-${l.id}`} checked={selected.includes(l.id)}
                    onClick={(e) => e.stopPropagation()} onChange={() => toggleSel(l.id)} />
                  <Actions lead={l} compact />
                </div>
              </div>
            </div>
          );
        })}
      </div>
      )}

      {/* Pagination */}
      <div className="flex flex-wrap items-center justify-between gap-2" data-testid="pagination">
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">Rows</span>
          <select data-testid="page-size" value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} className={`${fld} w-20`}>
            {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <button data-testid="page-prev" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
            className="rounded-lg border border-slate-200 p-1.5 disabled:opacity-40 dark:border-slate-700"><ChevronLeft className="h-4 w-4" /></button>
          <span data-testid="page-info">Page {data.page || page} of {data.pages || 1}</span>
          <button data-testid="page-next" disabled={page >= (data.pages || 1)} onClick={() => setPage((p) => p + 1)}
            className="rounded-lg border border-slate-200 p-1.5 disabled:opacity-40 dark:border-slate-700"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>

      {modal && <QuickModal modal={modal} users={opts.users} onClose={() => setModal(null)} onDone={() => { setModal(null); load(); }} />}
    </div>
  );
};

const QuickModal = ({ modal, users, onClose, onDone }) => {
  const { lead, type } = modal;
  const [v, setV] = useState({ note: '', date: '', time: '10:00', fuType: 'call', reason: '', status: lead.status, assignee: lead.assigned_to || '' });
  const [saving, setSaving] = useState(false);
  const titles = { note: 'Add Note', followup: 'Schedule Follow-up', reassign: 'Reassign Lead', status: 'Change Status' };

  const save = async () => {
    setSaving(true);
    try {
      if (type === 'note') {
        if (!v.note.trim()) { toast.error('Write a note first'); setSaving(false); return; }
        await noteAPI.create({ lead_id: lead.id, content: v.note.trim(), attachments: [] });
        toast.success('Note added');
      } else if (type === 'followup') {
        if (!v.date) { toast.error('Pick a date'); setSaving(false); return; }
        await followUpAPI.create({ lead_id: lead.id, scheduled_date: new Date(`${v.date}T${v.time}`).toISOString(),
          follow_up_type: v.fuType, notes: v.note });
        toast.success('Follow-up scheduled');
      } else if (type === 'reassign') {
        if (!v.assignee) { toast.error('Pick an executive'); setSaving(false); return; }
        await leadAPI.assign(lead.id, v.assignee, v.reason || undefined);
        toast.success('Lead reassigned');
      } else {
        const patch = { status: v.status };
        if (['lost', 'not_interested'].includes(v.status)) {
          if (!v.reason.trim()) { toast.error('A reason is required for this status'); setSaving(false); return; }
          patch.status_reason = v.reason.trim();
        }
        await leadAPI.update(lead.id, patch);
        toast.success('Status updated');
      }
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="quick-modal">
      <div className="w-full max-w-md space-y-3 rounded-xl bg-white p-5 dark:bg-slate-900">
        <div className="flex items-center justify-between">
          <h3 className="font-medium">{titles[type]} — {lead.customer_name}</h3>
          <button onClick={onClose} data-testid="quick-modal-close"><X className="h-4 w-4" /></button>
        </div>
        {type === 'note' && <textarea data-testid="quick-note-input" rows={4} value={v.note} onChange={(e) => setV({ ...v, note: e.target.value })}
          placeholder="Note visible on the Lead 360 timeline" className={fld} />}
        {type === 'followup' && <div className="space-y-2">
          <div className="flex gap-2">
            <input type="date" data-testid="quick-fu-date" value={v.date} onChange={(e) => setV({ ...v, date: e.target.value })} className={fld} />
            <input type="time" data-testid="quick-fu-time" value={v.time} onChange={(e) => setV({ ...v, time: e.target.value })} className={fld} />
          </div>
          <select data-testid="quick-fu-type" value={v.fuType} onChange={(e) => setV({ ...v, fuType: e.target.value })} className={fld}>
            {['call', 'whatsapp', 'meeting', 'site_visit', 'email'].map((t) => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
          </select>
          <input data-testid="quick-fu-note" value={v.note} onChange={(e) => setV({ ...v, note: e.target.value })} placeholder="Follow-up note" className={fld} />
        </div>}
        {type === 'reassign' && <div className="space-y-2">
          <select data-testid="quick-assignee" value={v.assignee} onChange={(e) => setV({ ...v, assignee: e.target.value })} className={fld}>
            <option value="">Select executive</option>
            {users.filter((u) => u.can_own_leads).map((u) => <option key={u.id} value={u.id}>{u.name} ({(u.role || '').replace('_', ' ')})</option>)}
          </select>
          <input data-testid="quick-reassign-reason" value={v.reason} onChange={(e) => setV({ ...v, reason: e.target.value })} placeholder="Reason (optional)" className={fld} />
        </div>}
        {type === 'status' && <div className="space-y-2">
          <select data-testid="quick-status" value={v.status} onChange={(e) => setV({ ...v, status: e.target.value })} className={fld}>
            {STATUS_OPTS.map(([s, l]) => <option key={s} value={s}>{l}</option>)}
          </select>
          {['lost', 'not_interested'].includes(v.status) && (
            <input data-testid="quick-status-reason" value={v.reason} onChange={(e) => setV({ ...v, reason: e.target.value })}
              placeholder="Reason (mandatory)" className={fld} />
          )}
        </div>}
        <div className="flex gap-2 pt-1">
          <button data-testid="quick-modal-save" disabled={saving} onClick={save} className="rounded-lg bg-primary px-4 py-2 text-sm text-white disabled:opacity-60">{saving ? 'Saving…' : 'Save'}</button>
          <button onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm dark:border-slate-700">Cancel</button>
        </div>
      </div>
    </div>
  );
};

export default LeadsPage;
